import React from 'react';
import Select, { components } from 'react-select';
import InstitutionBadge from '../InstitutionBadge.jsx';
import AccountLabel from './AccountLabel.jsx';
import { accountIdentity, accountSearchText, twinHints } from '../../../utilities/accountIdentity.js';

/**
 * A searchable account picker: accounts grouped under their institution's badge, each drawn by
 * AccountLabel, the chosen one on a single line. A native <select> could not tell two same-named
 * accounts apart -- an option is one line of text with nowhere to put the digits or the bank.
 *
 * `groups` is [{ label, badge, options }] (see accountGroup()). An option with no `identity` is a
 * choice that is not an account ("Not mapped") and is drawn as plain text.
 */
export default function AccountSelect({
	groups,
	value,
	onChange,
	placeholder = 'Choose an account',
	className = '',
	isDisabled = false,
	isClearable = false,
	name = undefined,
	autoFocus = false,
	wrapValue = false,
}) {
	return (
		<Select
			className={`account_select ${wrapValue ? 'account_select_wrap' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
			classNamePrefix='account_select'
			options={groups}
			value={value}
			onChange={onChange}
			isDisabled={isDisabled}
			isClearable={isClearable}
			name={name}
			autoFocus={autoFocus}
			components={{ Option: AccountOption, SingleValue: AccountSingleValue }}
			placeholder={placeholder}
			/** Institution groups make a long list scannable; search makes it navigable. */
			formatGroupLabel={(group) => (
				<div className='account_select__group_heading_label'>
					{group.badge && <InstitutionBadge {...group.badge} size={16} />}
					<span>{group.label}</span>
				</div>
			)}
			menuPlacement='auto'
			maxMenuHeight={360}
			/**
			 * The picker sits in a table cell or a modal, either of which would clip an absolutely
			 * positioned menu. Render it into the body instead; the z-index has to come through styles
			 * because a portalled menu is outside this component's DOM subtree.
			 */
			menuPortalTarget={typeof document === 'undefined' ? null : document.body}
			styles={wrapValue ? WRAPPING_STYLES : PLAIN_STYLES}
		/>
	);
}

const PLAIN_STYLES = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

/**
 * `wrapValue`: the chosen account's name may take a second line rather than be cut short, for a
 * picker whose whole job is telling similar accounts apart. react-select centres its value absolutely
 * on one line; here the value sits in the flow so the control grows with it, and the search input,
 * empty and caret-wide until someone types (when the value is hidden), is the one taken out of it.
 */
const WRAPPING_STYLES = {
	...PLAIN_STYLES,
	/** Holds its one-line height while someone types, when the value is hidden and only the input is left. */
	valueContainer: (base) => ({ ...base, minHeight: 36 }),
	singleValue: (base) => ({
		...base,
		position: 'static',
		top: 'auto',
		transform: 'none',
		maxWidth: '100%',
		overflow: 'visible',
		whiteSpace: 'normal',
		paddingTop: 2,
		paddingBottom: 2,
	}),
	input: (base) => ({
		...base,
		position: 'absolute',
		top: 0,
		bottom: 0,
		left: 8,
		margin: 0,
		paddingTop: 0,
		paddingBottom: 0,
		display: 'flex',
		alignItems: 'center',
	}),
};

/**
 * An option for one account. react-select filters on `label`, so it carries everything worth typing.
 * `hint` is the account's twinHints() entry, from the whole list the picker offers.
 */
export function accountOption(account, institution = null, hint = null) {
	const identity = accountIdentity(account, institution, hint);
	return { value: String(account.id), label: accountSearchText(identity), identity, account };
}

/**
 * A group of options under an institution: a PlaidAccount model or raw group, or null for "Other
 * accounts". `hints` is twinHints() of every account the picker offers, across all its groups.
 */
export function accountGroup(institution, accounts, hints = null) {
	const options = accounts.map((account) => accountOption(account, institution, hints ? hints.get(account.id) : null));
	const badge = options.length ? options[0].identity.badge : { institution: null, name: '', color: null };
	return {
		label: badge.name || 'Other accounts',
		badge,
		options,
	};
}

/**
 * Groups a flat list of accounts by the institution each carries (`account.institution`), in the
 * order they first appear.
 */
export function groupAccountsByInstitution(accounts) {
	const groups = [];
	const byName = {};
	const hints = twinHints(accounts);
	(accounts || []).forEach((account) => {
		const option = accountOption(account, null, hints.get(account.id));
		const label = option.identity.institutionName || 'Other accounts';
		if(!byName[label]) {
			byName[label] = { label, badge: option.identity.badge, options: [] };
			groups.push(byName[label]);
		}
		byName[label].options.push(option);
	});
	return groups;
}

/** The option among `groups` whose value is `value`, or null. */
export function findOption(groups, value) {
	const wanted = value === null || value === undefined ? '' : String(value);
	for(const group of groups) {
		for(const option of group.options) {
			if(option.value === wanted) {
				return option;
			}
		}
	}
	return null;
}

function AccountOption(props) {
	return (
		<components.Option {...props}>
			<OptionBody option={props.data} />
		</components.Option>
	);
}

function AccountSingleValue(props) {
	return (
		<components.SingleValue {...props}>
			<OptionBody option={props.data} compact />
		</components.SingleValue>
	);
}

function OptionBody({ option, compact = false }) {
	if(!option.identity) {
		return <span className='account_select_plain'>{option.label}</span>;
	}
	return <AccountLabel identity={option.identity} compact={compact} />;
}
